import { useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { MapPin, Plus, X, Check, Store, ChevronDown, ChevronUp } from 'lucide-react'
import { api } from '@/lib/api'
import { STORE_TYPE_LABELS } from '@/lib/utils'
import { useAppStore } from '@/store/app'
import type { GroceryStore, StoreType } from '@/types/models'

const STORE_TYPES: { id: StoreType; label: string; emoji: string }[] = [
  { id: 'grocery', label: 'Grocery', emoji: '🛒' },
  { id: 'asian_grocery', label: 'Asian Grocery', emoji: '🥢' },
  { id: 'warehouse', label: 'Warehouse Store', emoji: '🏭' },
  { id: 'specialty', label: 'Specialty', emoji: '🧑‍🍳' },
  { id: 'butcher', label: 'Butcher', emoji: '🥩' },
  { id: 'farmers_market', label: "Farmer's Market", emoji: '🥕' },
  { id: 'custom', label: 'Other', emoji: '📦' },
]

interface Props {
  selectedStoreIds: string[]
  onToggle: (storeId: string) => void
}

export default function StoreSelector({ selectedStoreIds, onToggle }: Props) {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()
  const { mapsEnabled } = useAppStore()
  const [addingManual, setAddingManual] = useState(false)
  const [newStore, setNewStore] = useState({ name: '', store_type: 'grocery' as StoreType })
  const [showNearby, setShowNearby] = useState(false)
  const [locationError, setLocationError] = useState<string | null>(null)
  const [nearbyParams, setNearbyParams] = useState<{ lat: number; lng: number } | null>(null)

  const { data: savedRes } = useQuery({
    queryKey: ['stores'],
    queryFn: async () => {
      const token = await getToken()
      return api.get<{ data: GroceryStore[] }>('/stores', token!)
    },
  })
  const savedStores = (savedRes as { data: GroceryStore[] } | undefined)?.data ?? []

  const { data: nearbyRes, isLoading: nearbyLoading } = useQuery({
    queryKey: ['stores-nearby', nearbyParams],
    queryFn: async () => {
      if (!nearbyParams) return { data: [] }
      const token = await getToken()
      return api.get<{ data: Array<{ place_id: string; name: string; address: string; latitude: number; longitude: number }> }>(
        `/stores/nearby?lat=${nearbyParams.lat}&lng=${nearbyParams.lng}`, token!
      )
    },
    enabled: !!nearbyParams && mapsEnabled,
  })
  const nearbyStores = (nearbyRes as { data: Array<{ place_id: string; name: string; address: string; latitude: number; longitude: number }> } | undefined)?.data ?? []

  const addStore = useMutation({
    mutationFn: async (store: { name: string; store_type: StoreType; place_id?: string; address?: string; latitude?: number; longitude?: number }) => {
      const token = await getToken()
      return api.post<{ data: GroceryStore }>('/stores', store, token!)
    },
    onSuccess: (res) => {
      const store = (res as { data: GroceryStore }).data
      queryClient.invalidateQueries({ queryKey: ['stores'] })
      onToggle(store.id)
      setAddingManual(false)
      setNewStore({ name: '', store_type: 'grocery' })
    },
  })

  function requestLocation() {
    setLocationError(null)
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setNearbyParams({ lat: pos.coords.latitude, lng: pos.coords.longitude })
        setShowNearby(true)
      },
      () => setLocationError('Location access denied. Please add stores manually.')
    )
  }

  async function addNearbyStore(nearby: { place_id: string; name: string; address: string; latitude: number; longitude: number }) {
    addStore.mutate({
      name: nearby.name,
      store_type: 'grocery',
      place_id: nearby.place_id,
      address: nearby.address,
      latitude: nearby.latitude,
      longitude: nearby.longitude,
    })
  }

  return (
    <div className="space-y-4">
      {/* Saved stores */}
      {savedStores.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Your Stores</p>
          <div className="space-y-2">
            {savedStores.map(store => {
              const selected = selectedStoreIds.includes(store.id)
              return (
                <button key={store.id} onClick={() => onToggle(store.id)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 text-left transition-colors ${
                    selected ? 'border-brand-600 bg-brand-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-base ${selected ? 'bg-brand-100' : 'bg-gray-100'}`}>
                      {STORE_TYPES.find(t => t.id === store.store_type)?.emoji ?? '🛒'}
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${selected ? 'text-brand-700' : 'text-gray-800'}`}>{store.name}</p>
                      <p className="text-xs text-gray-400">{STORE_TYPE_LABELS[store.store_type]}</p>
                    </div>
                  </div>
                  {selected && <Check className="w-4 h-4 text-brand-600" />}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Nearby stores — only shown when maps are enabled */}
      {mapsEnabled && (
        <div>
          <button onClick={showNearby ? () => setShowNearby(false) : requestLocation}
            className="flex items-center gap-2 text-sm text-brand-600 hover:underline font-medium"
          >
            <MapPin className="w-4 h-4" />
            {showNearby ? 'Hide' : 'Find'} nearby stores
            {showNearby ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {locationError && <p className="text-xs text-red-500 mt-1">{locationError}</p>}

          {showNearby && (
            <div className="mt-2 space-y-1.5">
              {nearbyLoading && (
                <div className="space-y-2">
                  {[...Array(3)].map((_, i) => <div key={i} className="h-12 bg-gray-100 rounded-xl animate-pulse" />)}
                </div>
              )}
              {nearbyStores.map((s) => {
                const alreadySaved = savedStores.some(ss => ss.place_id === s.place_id)
                return (
                  <div key={s.place_id} className="flex items-center justify-between px-3 py-2.5 rounded-xl bg-gray-50 border border-gray-200">
                    <div className="flex items-center gap-2">
                      <Store className="w-4 h-4 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-800">{s.name}</p>
                        <p className="text-xs text-gray-400 truncate max-w-[200px]">{s.address}</p>
                      </div>
                    </div>
                    {alreadySaved ? (
                      <span className="text-xs text-gray-400">Saved</span>
                    ) : (
                      <button onClick={() => addNearbyStore(s)} disabled={addStore.isPending}
                        className="text-xs text-brand-600 font-medium hover:underline"
                      >
                        + Add
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* Manual add */}
      <div>
        {!addingManual ? (
          <button onClick={() => setAddingManual(true)}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-brand-600 font-medium"
          >
            <Plus className="w-4 h-4" /> Add store manually
          </button>
        ) : (
          <div className="border border-gray-200 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-gray-700">Add Store</p>
              <button onClick={() => setAddingManual(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div>
              <label className="label">Store name</label>
              <input placeholder="e.g. Whole Foods, H Mart…" value={newStore.name}
                onChange={e => setNewStore(s => ({ ...s, name: e.target.value }))}
                className="input"
              />
            </div>
            <div>
              <label className="label">Store type</label>
              <div className="grid grid-cols-2 gap-1.5">
                {STORE_TYPES.map(t => (
                  <button key={t.id} onClick={() => setNewStore(s => ({ ...s, store_type: t.id }))}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                      newStore.store_type === t.id
                        ? 'border-brand-500 bg-brand-50 text-brand-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    <span>{t.emoji}</span> {t.label}
                  </button>
                ))}
              </div>
            </div>
            <button
              onClick={() => addStore.mutate({ name: newStore.name, store_type: newStore.store_type })}
              disabled={!newStore.name.trim() || addStore.isPending}
              className="btn-primary w-full"
            >
              {addStore.isPending ? 'Saving…' : 'Save Store'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
